import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline, GlobalStyles } from '@mui/material';

/*
 * Color tokens per mode. Components reference them as CSS variables
 * (e.g. `var(--v4-cyan)` or `rgba(var(--v4-fg-rgb),0.06)`), so static
 * style objects switch automatically when the mode changes.
 *
 * The token names are semantic slots shared by all UI variants (v4–v7):
 * `cyan` is the primary accent, `green`/`orange`/`red` are status colors,
 * `surface*` are card/menu backgrounds. Each variant fills the slots with
 * its own palette, fonts and shape (see `defineVariant`).
 */

const hexToRgb = (hex) => {
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
};
const rgbToHex = (rgb) => '#' + rgb.map(v => Math.round(v).toString(16).padStart(2, '0')).join('');
const luminance = (rgb) => {
  const [r, g, b] = rgb.map(v => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/* Slots that components also use as `rgba(var(--v4-<slot>-rgb),a)`. */
const RGB_SLOTS = ['text', 'cyan', 'green', 'red', 'red-soft', 'orange', 'purple'];

function completeTokens(t) {
  const out = { 'on-accent': '#fff', 'shadow-k': '1', 'glow-k': '1', ...t };
  for (const k of RGB_SLOTS) {
    if (!out[`${k}-rgb`] && out[k]) out[`${k}-rgb`] = hexToRgb(out[k]).join(',');
  }
  return out;
}

/**
 * A UI variant: palettes for both modes plus typography and shape.
 *   id, basePath, brand, tag, defaultMode, tokens: { dark, light },
 *   font, headingFont, fontsHref (Google Fonts CSS), radius,
 *   globalStyles(mode) → extra GlobalStyles object (optional)
 */
export function defineVariant(v) {
  return {
    defaultMode: 'dark',
    font: '"Inter","Roboto","Helvetica Neue",sans-serif',
    radius: 16,
    ...v,
    tokens: { dark: completeTokens(v.tokens.dark), light: completeTokens(v.tokens.light) },
  };
}

export const V4_VARIANT = defineVariant({
  id: 'v4',
  basePath: '',
  brand: 'EcoScope',
  tag: 'v4',
  tokens: {
    dark: {
      bg:          '#03050f',
      text:        '#e0ecff',
      'text-rgb':    '224,236,255',
      'muted-rgb':   '180,200,230',
      'fg-rgb':      '255,255,255',   // neutral overlays / hairlines
      'surface-rgb': '6,10,24',       // cards, dialogs
      'surface2-rgb':'4,7,20',        // menus, wells
      'shadow-rgb':  '0,0,0',
      cyan:        '#00d4ff',
      'cyan-light':'#66e8ff',
      'cyan-deep': '#005577',
      green:       '#39ff14',
      red:         '#ff3b30',
      'red-soft':  '#ff6b6b', 'red-soft-rgb': '255,80,80',
      orange:      '#ff9500',
      purple:      '#a855f7',
      gold:        '#ffd700',
      blue:        '#4488ff',
      teal:        '#00cfcf',
      neutral:     '#555555',
      whatsapp:    '#25d366',
    },
    light: {
      bg:          '#eef2f7',
      text:        '#0f1b2d',
      'muted-rgb':   '51,65,85',
      'fg-rgb':      '15,23,42',
      'surface-rgb': '255,255,255',
      'surface2-rgb':'248,250,252',
      'shadow-rgb':  '30,41,59',
      'shadow-k':    '0.3',
      cyan:        '#0891b2',
      'cyan-light':'#06b6d4',
      'cyan-deep': '#0e5f7a',
      green:       '#16a34a',
      red:         '#dc2626',
      'red-soft':  '#dc2626',
      orange:      '#d97706',
      purple:      '#7c3aed',
      gold:        '#ca8a04',
      blue:        '#2563eb',
      teal:        '#0d9488',
      neutral:     '#94a3b8',
      whatsapp:    '#128c4a',
      'glow-k':    '0.45',
    },
  },
});

const cssVars = (tokens) =>
  Object.fromEntries(Object.entries(tokens).map(([k, v]) => [`--v4-${k}`, v]));

/* Darken bright data colors (sensor/phase colors) until they read well on light surfaces. */
function toneForLight(hex, ink) {
  const src = hexToRgb(hex);
  const target = hexToRgb(ink);
  let out = src;
  for (let k = 0.05; luminance(out) > 0.2 && k <= 0.6; k += 0.05) {
    out = src.map((v, i) => v + (target[i] - v) * k);
  }
  return rgbToHex(out);
}

/**
 * Resolve a color for the active mode to a concrete value:
 * `var(--v4-xxx)` → token value, `rgba(var(--v4-xxx-rgb),a)` → rgba(),
 * plain hex → unchanged in dark mode, toned down in light mode.
 * Needed for SVG attributes, chart libs and anything persisted.
 */
export function resolveV4Color(color, mode, variant = V4_VARIANT) {
  if (typeof color !== 'string') return color;
  const tokens = variant.tokens[mode];
  let m = color.match(/^var\(--v4-([\w-]+)\)$/);
  if (m) return tokens[m[1]] ?? color;
  m = color.match(/^rgba\(var\(--v4-([\w-]+)\),\s*([\d.]+)\)$/);
  if (m) return tokens[m[1]] ? `rgba(${tokens[m[1]]},${m[2]})` : color;
  if (mode === 'light' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color)) return toneForLight(color, tokens.text);
  return color;
}

function buildTheme(variant, mode) {
  const t = variant.tokens[mode];
  const dark = mode === 'dark';
  const r = variant.radius;
  const headings = variant.headingFont ? { fontFamily: variant.headingFont } : {};
  return createTheme({
    palette: {
      mode,
      primary:    { main: t.cyan, dark: t['cyan-deep'], light: t['cyan-light'], contrastText: t['on-accent'] },
      secondary:  { main: t.green, contrastText: t['on-accent'] },
      success:    { main: t.green },
      warning:    { main: t.orange },
      error:      { main: t.red },
      info:       { main: t.purple },
      background: { default: t.bg, paper: `rgba(${t['surface-rgb']},0.94)` },
      divider:    `rgba(${t['cyan-rgb']},${dark ? 0.1 : 0.16})`,
      text: {
        primary:   `rgba(${t['text-rgb']},0.92)`,
        secondary: `rgba(${t['muted-rgb']},${dark ? 0.5 : 0.7})`,
      },
    },
    typography: {
      fontFamily: variant.font,
      h1: { fontWeight: 800, ...headings }, h2: { fontWeight: 700, ...headings }, h3: { fontWeight: 700, ...headings },
      h4: { fontWeight: 600, ...headings }, h5: { fontWeight: 600, ...headings }, h6: { fontWeight: 600, ...headings },
      subtitle1: { fontWeight: 500 }, subtitle2: { fontWeight: 500 },
      button: { fontWeight: 700, textTransform: 'none' },
    },
    shape: { borderRadius: r },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            background: t.bg,
            transition: 'background-color .3s ease',
            scrollbarWidth: 'thin',
            scrollbarColor: `rgba(${t['cyan-rgb']},0.25) transparent`,
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: `rgba(${t['cyan-rgb']},0.25)`, borderRadius: 4 },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: `rgba(${t['surface-rgb']},${dark ? 0.88 : 0.9})`,
            border: `1px solid rgba(${t['cyan-rgb']},${dark ? 0.1 : 0.14})`,
            backdropFilter: 'blur(20px)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: Math.min(r, 10), fontWeight: 700 },
          containedPrimary: {
            color: t['on-accent'],
            background: `linear-gradient(135deg,${t.cyan} 0%,${t['cyan-deep']} 100%)`,
            boxShadow: `0 4px 20px rgba(${t['cyan-rgb']},${dark ? 0.3 : 0.2})`,
            '&:hover': {
              background: `linear-gradient(135deg,${t['cyan-light']} 0%,${t.cyan} 100%)`,
              boxShadow: `0 6px 28px rgba(${t['cyan-rgb']},${dark ? 0.45 : 0.3})`,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: `rgba(${t['cyan-rgb']},${dark ? 0.08 : 0.14})` } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: Math.min(r, 8), fontWeight: 700 } },
      },
    },
  });
}

const ColorModeContext = createContext({
  mode: 'dark', toggleMode: () => {}, variant: V4_VARIANT,
});

export function useV4ColorMode() {
  return useContext(ColorModeContext);
}

/** The active UI variant (palette, brand, base path). */
export function useV4Variant() {
  return useContext(ColorModeContext).variant;
}

/** Route prefix of the active variant: '' for the default UI, '/v5' etc. otherwise. */
export function useBasePath() {
  return useV4Variant().basePath;
}

/** Returns a resolver that turns token vars into concrete colors for the active mode. */
export function useV4Resolve() {
  const { mode, variant } = useV4ColorMode();
  return useCallback((c) => resolveV4Color(c, mode, variant), [mode, variant]);
}

function initialMode(variant) {
  try {
    const saved = localStorage.getItem(`${variant.id}-color-mode`);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch { /* storage unavailable */ }
  return variant.defaultMode;
}

function useGoogleFonts(href) {
  useEffect(() => {
    if (!href || document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }, [href]);
}

export function V4ThemeProvider({ variant = V4_VARIANT, children }) {
  const [mode, setMode] = useState(() => initialMode(variant));
  useGoogleFonts(variant.fontsHref);

  useEffect(() => {
    try { localStorage.setItem(`${variant.id}-color-mode`, mode); } catch { /* ignore */ }
  }, [mode, variant.id]);

  const ctx   = useMemo(() => ({
    mode, variant, toggleMode: () => setMode(m => (m === 'dark' ? 'light' : 'dark')),
  }), [mode, variant]);
  const theme = useMemo(() => buildTheme(variant, mode), [variant, mode]);
  const extra = variant.globalStyles ? variant.globalStyles(mode) : null;

  return (
    <ColorModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{ ':root': { ...cssVars(variant.tokens[mode]), colorScheme: mode } }} />
        {extra && <GlobalStyles styles={extra} />}
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
